/**
 * Import and export of layout files. Imported files are untrusted input
 * (secure-code-guardian): size-capped, parsed defensively, validated by the shared
 * schema, and applied all-or-nothing. Export re-validates and writes the schema's key
 * order so files diff cleanly.
 */
import { LayoutFileSchema, parseLayoutFile, safeParseWith, ValidationError, type LayoutFile, type ValidationIssue } from '@ioda/shared';

/** Real layouts are a few KB; anything this large is not a layout. */
export const MAX_IMPORT_BYTES = 256 * 1024;

export type ImportResult = { ok: true; layout: LayoutFile } | { ok: false; issues: string[] };

/**
 * Parses and validates a layout file's text. `layoutId` must match the file stem.
 * @param byteSize The file's size in bytes, checked before parsing.
 */
export function parseLayoutText(text: string, fileName: string, byteSize: number): ImportResult {
  if (byteSize > MAX_IMPORT_BYTES) {
    return { ok: false, issues: [`${fileName} is ${Math.ceil(byteSize / 1024)} KB; layout files are at most ${MAX_IMPORT_BYTES / 1024} KB`] };
  }
  if (!/\.json$/i.test(fileName)) return { ok: false, issues: [`${fileName} is not a .json file`] };
  let data: unknown;
  try {
    data = JSON.parse(text.charCodeAt(0) === 0xfeff ? text.slice(1) : text);
  } catch (e) {
    return { ok: false, issues: [`${fileName} is not valid JSON: ${(e as Error).message}`] };
  }
  try {
    return { ok: true, layout: parseLayoutFile(data, fileName) };
  } catch (e) {
    if (e instanceof ValidationError) return { ok: false, issues: e.issues.map((i) => `${i.path}: ${i.message}`) };
    throw e;
  }
}

/** Reads a user-picked file. */
export async function importLayoutFile(file: File): Promise<ImportResult> {
  if (file.size > MAX_IMPORT_BYTES) return parseLayoutText('', file.name, file.size);
  return parseLayoutText(await file.text(), file.name, file.size);
}

/** Every schema issue in the current layout; empty when it can be exported. */
export function validateLayout(layout: LayoutFile): ValidationIssue[] {
  const r = safeParseWith(LayoutFileSchema, layout);
  return r.ok ? [] : r.issues;
}

/** The download name: the file stem is the layout ID (SPEC §5.2). */
export function exportFileName(layout: LayoutFile): string {
  return `${layout.layoutId}.json`;
}

/**
 * Serializes a valid layout: the schema's key order, 2-space indent, trailing newline.
 * @throws ValidationError if the layout is invalid.
 */
export function serializeLayout(layout: LayoutFile): string {
  const issues = validateLayout(layout);
  if (issues.length > 0) throw new ValidationError('layout', issues);
  // Parsing returns keys in schema order, which makes exports stable and diffable.
  return `${JSON.stringify(LayoutFileSchema.parse(layout), null, 2)}\n`;
}
